/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Shimmer, Text, Tooltip } from '@pukka/ui-components';
import * as Collapsible from '@radix-ui/react-collapsible';
import { debounce } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio';

import { BrandsPersistedState, BrandsState } from '../Brands.state';
import { BrandsFormState } from '../BrandsForm';
import { BrandsNoData } from '../BrandsNoData';
import { BrandsAssignModal } from '../Modal';
import { BrandsAssignState } from '../Modal/BrandsAssign.state';

import { BrandRow } from './BrandRow';
import { ParentBrandName } from './ParentBrandName';
import Styles from './styles.module.css';

/* istanbul ignore next */
const isVisibleInViewport = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

function RowLoading({ variant = 'brand', withCheckbox }: { variant?: 'brand' | 'parent'; withCheckbox?: boolean }) {
  const isBrand = variant === 'brand';

  return (
    <div className="flex h-14 items-center hover:bg-neutral-50">
      <div className="w-[72px]" />

      {withCheckbox && (
        <div className={twMerge('py-4', isBrand ? 'pl-24 pr-6' : 'px-6')}>
          <Checkbox size="md" disabled />
        </div>
      )}

      <div className="group flex min-w-0 flex-1 items-center gap-1 px-4">
        <Shimmer size="lg" />
      </div>
    </div>
  );
}

function BrandsTableBody({
  state = BrandsState,
  persistedState = BrandsPersistedState,
  formState = BrandsFormState,
  assignState = BrandsAssignState,
  withCheckbox,
}: {
  state?: typeof BrandsState;
  persistedState?: typeof BrandsPersistedState;
  formState?: typeof BrandsFormState;
  assignState?: typeof BrandsAssignState;
  withCheckbox: boolean;
}) {
  const { t } = useTranslation('library');
  const [openAssignModal, setOpenAssignModal] = useState(false);

  const { brandListByParent, brandListByKeyword } = useSnapshot(persistedState) as typeof BrandsPersistedState;

  const { checkedBrand, checkedParent, loadingParentId, status, expandedParentId, keyword, keywordType, filteredParentList } =
    useSnapshot(state);

  const { handleToggleCollapsible, handleCheckParent, handleCheckBrand } = state;

  const { handleClickEditBrand, handleClickEditParent, handleClickCreateBrand } = formState;

  const tlAssignPrefix = 'brands.assignModal.parent';

  const handleScroll = debounce(() => {
    if (expandedParentId && brandListByParent[expandedParentId]) {
      const thirdToLastIdx = brandListByParent[expandedParentId].results.length - 4;

      const thirdToLastBrand = document.getElementById(`brand-${brandListByParent[expandedParentId].results[thirdToLastIdx]?.id}`);

      /* istanbul ignore next */
      if (!thirdToLastBrand) {
        return;
      }

      const isVisible = isVisibleInViewport(thirdToLastBrand);

      if (isVisible && !brandListByParent[expandedParentId].completed) {
        state.fetchMoreBrandsByParent(expandedParentId);
      }
    }
  }, 500);

  const handleScrollKeywordResult = debounce(() => {
    const brandSearchScroll = document.getElementById('brand-search-scroll');

    /* istanbul ignore next */
    if (!brandSearchScroll) {
      return;
    }

    const isAtBottom = brandSearchScroll.scrollHeight - brandSearchScroll.clientHeight <= brandSearchScroll.scrollTop + 300;
    const isCompleted = brandListByKeyword?.completed;

    if (isAtBottom && !isCompleted) {
      state.handleFetchMoreByKeyword();
    }
  }, 500);

  const handleAddBrandUnderPrincipal = (id: number, name: string) => {
    handleClickCreateBrand({ id, name });
  };

  const handleOpenAssignParentShortcut = (brand: { id: number; name: string }, parent: { id: number; name: string }) => {
    formState.editId = brand.id;
    formState.brandName = brand.name;

    assignState.initParentList({ id: parent.id, name: parent.name }, () => setOpenAssignModal(true), persistedState);
  };

  const handleSubmitAssignParentShortcut = () => {
    formState.handleSubmitShortcutAssign();
    setOpenAssignModal(false);
  };

  if (keyword && keywordType === 'brand') {
    const brandList = brandListByKeyword?.results;
    const totalCount = brandListByKeyword?.total_count;
    const showLoading = status.brandListByKeyword === 'loading' || (brandList || []).length < (totalCount || 0);

    if (status.brandListByKeyword === 'success' && keyword && brandList?.length === 0) {
      return <BrandsNoData title={t('brands.noKeywordFound.title', { keyword })} description={t('brands.noKeywordFound.description')} />;
    }

    return (
      <div id="brand-search-scroll" onScroll={handleScrollKeywordResult} className="h-[calc(100vh-224px)] overflow-scroll">
        {brandListByKeyword?.results?.map((brand) => {
          const isParentChecked = checkedParent.has(brand.principal_id);

          return (
            <BrandRow
              key={brand.id}
              brand={brand}
              disableCheckbox={isParentChecked}
              withCheckbox={withCheckbox}
              checked={checkedBrand.has(brand.id) || isParentChecked}
              onChangeCheckbox={handleCheckBrand}
              onClickAssignParent={handleOpenAssignParentShortcut}
              onClickEdit={handleClickEditBrand}
            />
          );
        })}

        {showLoading && Array.from({ length: 3 }).map((_, i) => <RowLoading key={i} withCheckbox={withCheckbox} />)}

        <BrandsAssignModal
          open={openAssignModal}
          onToggleModal={setOpenAssignModal}
          heading={t(`${tlAssignPrefix}.title`)}
          description={t(`${tlAssignPrefix}.description`)}
          searchPlaceholder={t(`${tlAssignPrefix}.searchPlaceholder`)}
          variant="parent"
          state={assignState}
          onSubmit={handleSubmitAssignParentShortcut}
        />
      </div>
    );
  }

  return (
    <div id="parent-scroll" className="h-[calc(100vh-230px)] overflow-scroll" onScroll={handleScroll}>
      {status.parentList === 'loading' &&
        Array.from({ length: 3 }).map((_, i) => <RowLoading key={i} variant="parent" withCheckbox={withCheckbox} />)}

      {status.parentList === 'success' && filteredParentList?.length === 0 && keyword && (
        <BrandsNoData title={t('brands.noKeywordFound.title', { keyword })} description={t('brands.noKeywordFound.description')} />
      )}

      {filteredParentList?.map((parentBrand) => {
        const isParentChecked = checkedParent.has(parentBrand.id);
        const brandCount = parentBrand.brand_count;
        const isUnprincipal = parentBrand.id === -1;
        const isCollapsible = brandCount > 0 || isUnprincipal;

        const parentStyles = twMerge(
          'sticky top-0 flex h-14 items-center bg-white hover:bg-neutral-50',
          isParentChecked ? 'bg-light-blue-50' : '',
          !isUnprincipal ? 'group hover:cursor-pointer' : '',
        );

        const checkParent = () => {
          handleCheckParent(parentBrand.id);
        };

        const handleClickRow = () => {
          if (!isUnprincipal) {
            handleClickEditParent(parentBrand.id, parentBrand.name);
          }
        };

        const handleClickChevron = (e: React.MouseEvent) => {
          e.stopPropagation();
          handleToggleCollapsible(parentBrand.id);
        };

        if (isCollapsible) {
          const isOpen = expandedParentId === parentBrand.id;

          const brandList = brandListByParent[parentBrand.id]?.results;

          return (
            <Collapsible.Root key={parentBrand.id} open={isOpen}>
              <div className={parentStyles} data-testid={`parent-${parentBrand.id}`} onClick={handleClickRow}>
                <div className="p-4">
                  <Tooltip
                    trigger={
                      <Collapsible.Trigger asChild onClick={handleClickChevron} data-testid={`collapsible-${parentBrand.id}`}>
                        <Button icon="chevron-down" variant="nude" className={twMerge('p-0', isOpen ? '' : 'rotate-180')} size="m" />
                      </Collapsible.Trigger>
                    }
                  >
                    <Text color="white" variant="caption-1">
                      {isOpen ? 'close' : 'open'}
                    </Text>
                  </Tooltip>
                </div>

                {withCheckbox && (
                  <div className="px-6 py-4">
                    <Checkbox
                      size="md"
                      data-testid={`parent-checkbox-${parentBrand.id}`}
                      checked={isParentChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={checkParent}
                    />
                  </div>
                )}

                <ParentBrandName
                  id={parentBrand.id}
                  name={parentBrand.name}
                  totalBrands={parentBrand.brand_count}
                  onClickAddBrand={handleAddBrandUnderPrincipal}
                  onClickEdit={handleClickEditParent}
                />
              </div>

              <Collapsible.Content className={Styles.CollapsibleContent}>
                {brandList?.map((brand) => (
                  <BrandRow
                    key={brand.id}
                    brand={brand}
                    withCheckbox={withCheckbox}
                    checked={checkedBrand.has(brand.id) || isParentChecked}
                    disableCheckbox={isParentChecked}
                    onChangeCheckbox={handleCheckBrand}
                    onClickAssignParent={handleOpenAssignParentShortcut}
                    onClickEdit={handleClickEditBrand}
                  />
                ))}
                {(loadingParentId === parentBrand.id || brandList?.length < brandCount) &&
                  Array.from({ length: 3 }).map((_, i) => <RowLoading key={i} withCheckbox={withCheckbox} />)}
              </Collapsible.Content>
            </Collapsible.Root>
          );
        }

        return (
          <div key={parentBrand.id}>
            <div className={parentStyles} onClick={handleClickRow}>
              <div className="w-[72px]" />

              {withCheckbox && (
                <div className="px-6 py-4">
                  <Checkbox
                    size="md"
                    data-testid={`parent-checkbox-${parentBrand.id}`}
                    checked={isParentChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={checkParent}
                  />
                </div>
              )}

              <ParentBrandName
                id={parentBrand.id}
                name={parentBrand.name}
                totalBrands={parentBrand.brand_count}
                onClickAddBrand={handleAddBrandUnderPrincipal}
                onClickEdit={handleClickEditParent}
              />
            </div>
          </div>
        );
      })}

      <BrandsAssignModal
        open={openAssignModal}
        onToggleModal={setOpenAssignModal}
        heading={t(`${tlAssignPrefix}.title`)}
        description={t(`${tlAssignPrefix}.description`)}
        searchPlaceholder={t(`${tlAssignPrefix}.searchPlaceholder`)}
        variant="parent"
        state={assignState}
        onSubmit={handleSubmitAssignParentShortcut}
      />
    </div>
  );
}

export { BrandsTableBody, RowLoading as BrandsRowLoading };
